import { Alert, AlertConfigResponse, CommunicationMethod } from '../types';

import {
  normalizeTimezone,
  convertHtmlToSlackMarkdown,
  convertHtmlToEmailHtml,
  convertHtmlToPlainText,
} from './helpers';

// Mapping function to convert API response to frontend Alert format
export const mapApiResponseToAlert = (apiResponse: AlertConfigResponse): Alert => {
  // Convert communication methods from API format to frontend format
  const communicationMethods: CommunicationMethod[] = [
    {
      id: 'slack',
      type: 'Slack',
      selected: apiResponse.enable_slack,
    },
    {
      id: 'email',
      type: 'Email',
      selected: apiResponse.enable_email,
    },
    {
      id: '3',
      type: 'LIFT notifications for a Brand',
      selected:
        apiResponse.enable_lift_notification &&
        apiResponse.recipients?.web?.some(r => r.recipient_type === 'web_brand'),
    },
    {
      id: '4',
      type: 'LIFT notifications for a user',
      selected:
        apiResponse.enable_lift_notification &&
        apiResponse.recipients?.web?.some(r => r.recipient_type === 'web_user'),
    },
  ];

  // Extract selected members from recipients.users
  const selectedMembers = apiResponse.recipients?.users?.map(user => user.email) || [];

  // Extract Slack recipients and convert to UI format
  const slackRecipients =
    apiResponse.recipients?.slack?.map(recipient => {
      const type = recipient.recipient_type === 'slack_user' ? 'user' : 'channel';
      return JSON.stringify({
        type,
        id: recipient.recipient_id,
        name: recipient.recipient_value,
      });
    }) || [];

  // Map status based on is_active
  const status: 'Active' | 'Inactive' | 'Paused' = apiResponse.is_active ? 'Active' : 'Inactive';

  // Get brand names - if multiple brands, join them with commas
  const brandNames = apiResponse.brands?.map(brand => brand.brand_name).join(', ') || 'N/A';

  // Get the first threshold value (for display purposes)
  // Note: threshold_config is now nested, so we need to access it properly
  const firstThreshold =
    apiResponse.brands?.[0]?.threshold_config &&
    Array.isArray(apiResponse.brands[0].threshold_config) &&
    apiResponse.brands[0].threshold_config[0] &&
    Array.isArray(apiResponse.brands[0].threshold_config[0]) &&
    apiResponse.brands[0].threshold_config[0][0]
      ? apiResponse.brands[0].threshold_config[0][0].value
      : undefined;

  // For table display, show the alert NAME in the "ALERT MESSAGE" column as requested
  const alertMessageForTable = apiResponse.name;

  // For step 4 (MessageTemplateStep), use message_template.msg for the Alert Prompt Message
  const alertMessageForForm = apiResponse.message_template?.msg || '';

  const ownerUserId = apiResponse.user_id;

  // Flatten nested threshold_config structure for UI consumption
  // Backend returns: threshold_config: [[{...connect_to_next...}], [{...}]]
  // UI needs: threshold_config: [{...logicalOperator...}]
  // Track if any threshold uses fiscal calendar
  const hasFiscalCalendarRef = { current: false };
  const transformedBrands = apiResponse.brands?.map(brand => {
    if (!brand.threshold_config || !Array.isArray(brand.threshold_config)) {
      return brand;
    }

    // Flatten the nested array structure
    const flattenedThresholds: any[] = [];

    brand.threshold_config.forEach((group, groupIndex) => {
      if (!Array.isArray(group)) return;

      group.forEach((threshold, thresholdIndex) => {
        const isLastInGroup = thresholdIndex === group.length - 1;
        const isLastGroup = groupIndex === brand.threshold_config.length - 1;

        // Convert connect_to_next to logicalOperator
        let logicalOperator: 'AND' | 'OR' | 'NONE' | undefined;

        if (threshold.connect_to_next) {
          // Has connect_to_next, use it as logicalOperator
          logicalOperator = threshold.connect_to_next as 'AND' | 'OR';
        } else if (isLastInGroup && !isLastGroup) {
          // Last in group but not last overall = new group starts = NONE
          logicalOperator = 'NONE';
        } else {
          // Last threshold overall or no operator specified
          logicalOperator = undefined;
        }

        // Convert date from DD/MM/YYYY to YYYY-MM-DD format for date picker
        const convertDateToFrontend = (dateStr: string | null | undefined): string | undefined => {
          if (!dateStr) {
            return undefined;
          }
          // Check if already in YYYY-MM-DD format
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return dateStr;
          }
          // Convert from DD/MM/YYYY to YYYY-MM-DD
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const [day, month, year] = parts;
            const converted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            return converted;
          }
          return undefined;
        };

        // Helper function to convert fiscal dates (DD/MM/YYYY) to ISO string format for storage
        const convertFiscalDatesToISO = (
          startDate: string | null | undefined,
          endDate: string | null | undefined
        ): string => {
          if (!startDate || !endDate) return '';
          const startDateStr = convertDateToFrontend(startDate);
          const endDateStr = convertDateToFrontend(endDate);
          if (startDateStr && endDateStr) {
            const startISO = new Date(startDateStr).toISOString();
            const endISO = new Date(endDateStr).toISOString();
            return `${startISO}|${endISO}`;
          }
          return '';
        };

        // Helper function to extract and set calendar_type from threshold
        const extractCalendarType = (
          threshold: any,
          thresholdObj: any,
          hasFiscalCalendarRef: { current: boolean }
        ): void => {
          if (
            'calendar_type' in threshold &&
            threshold.calendar_type !== null &&
            threshold.calendar_type !== undefined
          ) {
            thresholdObj.calendarType = threshold.calendar_type as
              | 'fiscal'
              | 'calendar'
              | 'relative';
            if (threshold.calendar_type === 'fiscal') {
              hasFiscalCalendarRef.current = true;
            }
            // For relative calendar type, store the comparision_date as comparisonDate
            if (threshold.calendar_type === 'relative' && threshold.comparision_date) {
              thresholdObj.comparisonDate = threshold.comparision_date;
            }
          }
        };

        // Determine if this is a pacing threshold (has dimension_name or dimension array),
        // EMQ threshold (has pixel_id/event_name), or taxonomy threshold (has taxonomy_type)
        const isNewFormatPacing = Array.isArray(threshold.metric) && threshold.metric.length > 0;
        const isOldFormatPacing = Boolean(threshold.dimension_name);
        const isPacingThreshold = isNewFormatPacing || isOldFormatPacing;
        const isEMQThreshold = Boolean(threshold.pixel_id || threshold.event_name);
        const isTaxonomyThreshold = Boolean(threshold.taxonomy_type);

        // Ensure operator is preserved for pacing alerts (can be +, -, or +-)
        // For other alert types, default to < if missing
        // Preserve operator value - check for null, undefined, or empty string
        let operatorValue = threshold.operator;
        if (operatorValue === null || operatorValue === undefined || operatorValue === '') {
          if (isPacingThreshold) {
            operatorValue = '+'; // Default for pacing if missing
          } else {
            operatorValue = '<'; // Default for other types
          }
        } else {
          // Convert to string to ensure consistency
          operatorValue = String(operatorValue).trim();
        }

        const thresholdObj: any = {
          threshold: threshold.value,
          value: threshold.value,
          brand_id: threshold.brand_id,
          operator: operatorValue,
          comparison_period: threshold.comparison_period,
          logicalOperator, // Add logicalOperator for UI
        };

        if (isPacingThreshold) {
          if (isNewFormatPacing) {
            // New format: metric and dimension are arrays
            const firstMetric = threshold.metric[0];

            thresholdObj.metric = firstMetric.name;
            thresholdObj.metricsId = firstMetric.metrics_id || null;
            thresholdObj.metricsCategory = firstMetric.type || 'standard';

            // Handle multiple dimensions - convert to sheet config format
            // New format: each dimension object has name (child), type (parent), value (array), category
            if (Array.isArray(threshold.dimension) && threshold.dimension.length > 0) {
              let firstDimension: any = null;
              let isAllDimension = false;

              // Build a map of type -> values for dimension combination key
              const dimensionMap: Record<string, string[]> = {};

              threshold.dimension.forEach((dim: any) => {
                if (dim && dim.name && dim.type) {
                  // Use first dimension for metadata
                  if (!firstDimension) {
                    firstDimension = dim;
                  }

                  // Check if this is "All Dimension" - name is "All" and type is "All" or "ALL_DIMENSION"
                  const isAllDim =
                    dim.name === 'All' && (dim.type === 'All' || dim.type === 'ALL_DIMENSION');
                  if (isAllDim) {
                    isAllDimension = true;
                    dimensionMap['All'] = ['All Dimension'];
                  } else {
                    // In new format: dim.name is the child value, dim.type is the parent
                    const parentName = String(dim.type).trim();
                    const childName = String(dim.name).trim();

                    if (parentName && childName) {
                      if (!dimensionMap[parentName]) {
                        dimensionMap[parentName] = [];
                      }
                      dimensionMap[parentName].push(childName);
                    }
                  }
                }
              });

              // Create dimension combination key (matches getDimensionCombinationKey format)
              // Format: "Platform:Google|Tactic:Brand" (sorted alphabetically)
              const sortedEntries = Object.entries(dimensionMap)
                .filter(([, values]) => Array.isArray(values) && values.length > 0)
                .sort(([a], [b]) => a.localeCompare(b));
              const dimensionKey = sortedEntries
                .map(([dimType, values]) => `${dimType}:${values.sort().join(',')}`)
                .join('|');

              if (dimensionKey) {
                // Store the dimension key for dropdown selection
                thresholdObj.dimension = dimensionKey;
                // Store the full dimension array as JSON for payload
                thresholdObj.dimensionValue = JSON.stringify(threshold.dimension);
              }

              // Use first dimension's metadata
              if (firstDimension) {
                thresholdObj.dimensionId = firstDimension.dimension_id || null;
                // For "All Dimension", set type to "All" regardless of what's in the API
                if (isAllDimension) {
                  thresholdObj.dimensionType = 'All';
                  thresholdObj.dimensionCategory = 'standard';
                } else {
                  // Use the type from the API response instead of hardcoding 'sheet_config'
                  thresholdObj.dimensionType = firstDimension.type || 'sheet_config';
                  thresholdObj.dimensionCategory = firstDimension.category || 'standard';
                }
              }
            }

            // Handle dates from API response
            // startDate stores preset identifier (if comparision_date exists) or empty string
            // endDate always stores dates in "startISO|endISO" format for DatePickerDateRange
            if (threshold.comparision_date) {
              thresholdObj.startDate = threshold.comparision_date;
            } else {
              // No preset identifier - leave startDate empty
              thresholdObj.startDate = '';
            }
            // Always store actual dates in endDate as ISO format
            thresholdObj.endDate = convertFiscalDatesToISO(
              threshold.start_date,
              threshold.end_date
            );
            // Store calendar_type from API response
            extractCalendarType(threshold, thresholdObj, hasFiscalCalendarRef);
            // Store pacing_value_type from API response; backend sends "percentage" | "count", map to UI "percentage" | "number"
            // Treat missing as "percentage" per backend
            if (threshold.pacing_value_type) {
              thresholdObj.pacingValueType =
                threshold.pacing_value_type === 'count' ? 'number' : threshold.pacing_value_type;
            } else {
              thresholdObj.pacingValueType = 'percentage';
            }
          } else {
            // Old format: use metrics_name, dimension_name, etc.
            thresholdObj.metric = threshold.metrics_name || threshold.metric;
            thresholdObj.dimension = threshold.dimension_name;
            thresholdObj.dimensionId = threshold.dimension_id || null;
            thresholdObj.dimensionType = threshold.dimension_type || null;
            thresholdObj.dimensionValue = threshold.dimension_value || null;
            thresholdObj.dimensionCategory = threshold.dimension_category || null;
            thresholdObj.metricsId = threshold.metrics_id || null;
            thresholdObj.metricsCategory = threshold.metrics_category || null;
            // Handle dates from API response
            if (threshold.comparision_date) {
              thresholdObj.startDate = threshold.comparision_date;
            } else {
              thresholdObj.startDate = '';
            }
            // Always store actual dates in endDate as ISO format
            thresholdObj.endDate = convertFiscalDatesToISO(
              threshold.start_date,
              threshold.end_date
            );
            // Store calendar_type from API response
            extractCalendarType(threshold, thresholdObj, hasFiscalCalendarRef);
            // Store pacing_value_type from API response; backend sends "percentage" | "count", map to UI "percentage" | "number"
            // Treat missing as "percentage" per backend
            if (threshold.pacing_value_type) {
              thresholdObj.pacingValueType =
                threshold.pacing_value_type === 'count' ? 'number' : threshold.pacing_value_type;
            } else {
              thresholdObj.pacingValueType = 'percentage';
            }
          }
        } else if (isTaxonomyThreshold) {
          // Taxonomy threshold: use taxonomy_type and taxonomy_value_type
          thresholdObj.metric = threshold.metric || 'taxonomy_score';
          thresholdObj.taxonomyType = threshold.taxonomy_type;

          // Infer taxonomy_value_type from the value if not provided
          let taxonomyValueType = threshold.taxonomy_value_type;
          if (!taxonomyValueType) {
            // If value is a string that looks like a date, it's updated_at
            if (typeof threshold.value === 'string' && threshold.value.includes('T')) {
              taxonomyValueType = 'updated_at';
            } else {
              // Otherwise, default to percentage (could also infer from value range)
              taxonomyValueType = 'percentage';
            }
          }

          thresholdObj.taxonomyValueType = taxonomyValueType;

          // Handle updated_at date parsing
          if (taxonomyValueType === 'updated_at' && typeof threshold.value === 'string') {
            try {
              const date = new Date(threshold.value);
              if (!isNaN(date.getTime()) && date.getFullYear() > 1970) {
                thresholdObj.updatedAtDate = date.toISOString().split('T')[0];
                thresholdObj.updatedAtTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                thresholdObj.threshold = threshold.value; // Store ISO string as threshold
              } else {
                console.error('Invalid date string for taxonomy updated_at:', threshold.value);
                thresholdObj.threshold = threshold.value;
              }
            } catch (e) {
              console.error('Error parsing taxonomy updated_at date:', threshold.value, e);
              thresholdObj.threshold = threshold.value;
            }
          }
        } else if (isEMQThreshold) {
          // EMQ threshold: use metric, pixel_id, event_name
          thresholdObj.metric = threshold.metric;
          thresholdObj.pixelId = threshold.pixel_id;
          thresholdObj.eventName = threshold.event_name;
          thresholdObj.eventId = threshold.event_id;
          thresholdObj.pixelName = threshold.pixel_name;
        } else {
          // Fallback: use metric or metrics_name
          thresholdObj.metric = threshold.metrics_name || threshold.metric;
        }

        flattenedThresholds.push(thresholdObj);
      });
    });

    return {
      ...brand,
      threshold_config: flattenedThresholds,
    };
  });

  // Store fiscal calendar flag in the alert object
  const mappedAlert = {
    id: apiResponse.id.toString(),
    name: apiResponse.name,
    hasFiscalCalendar: hasFiscalCalendarRef.current, // Flag indicating if alert uses fiscal calendar
    description: apiResponse.description,
    creator: apiResponse.user_name ?? '',
    ownerUserId,
    ownerEmail: apiResponse.user_email ?? '',
    creatorAvatar:
      'https://storage.googleapis.com/ltp-static-data/drawer-icons/internal%20user.svg',
    toolName: apiResponse.alert_type_display_name,
    brandName: brandNames,
    dateTime: apiResponse.last_triggered_at || apiResponse.created_at,
    status,
    alertMessage: alertMessageForTable, // For table display
    alertMessageForForm: alertMessageForForm, // For step 4 form field
    threshold: firstThreshold,
    communicationMethods,
    selectedMembers,
    slackRecipients,
    cadence: apiResponse.cadence,
    createdAt: apiResponse.created_at,
    updatedAt: apiResponse.updated_at,
    common_id: apiResponse.common_id,
    alert_type_id: apiResponse.alert_type_id,
    recipients: apiResponse.recipients,
    messageTemplates: {
      slack: apiResponse.message_template?.slack || '',
      email: apiResponse.message_template?.email || '',
      web: apiResponse.message_template?.web || '',
    },
    schedule_config: apiResponse.schedule_config
      ? {
          ...apiResponse.schedule_config,
          timezone: normalizeTimezone(apiResponse.schedule_config.timezone),
        }
      : undefined,
    brands: transformedBrands,
    cron_info: apiResponse.cron_info,
  };

  return mappedAlert;
};

// Mapping function to convert frontend AlertFormData to API format
export const buildThresholdConfigs = (
  formData: any,
  selectedBrands?: any[],
  brandIds?: number[]
): any[][] => {
  const resolvedBrandIds =
    (brandIds && brandIds.length > 0
      ? brandIds
      : selectedBrands
          ?.map(brand => brand.brandId)
          ?.filter((id: number | undefined) => typeof id === 'number')) ?? [];

  // Create threshold configs from all thresholds selected in Step 2
  // New structure: List[List[ThresholdConfig]] - nested array for logical operators
  //
  // Example: If thresholds are:
  //   T1 (logicalOperator: 'AND')
  //   T2 (logicalOperator: 'OR')
  //   T3 (logicalOperator: 'NONE')
  //   T4 (logicalOperator: undefined)
  //
  // Result will be:
  //   [
  //     [T1{connect_to_next: 'AND'}, T2{connect_to_next: 'OR'}, T3],  // Group 1: (T1 AND T2 OR T3)
  //     [T4]                                                            // Group 2: T4
  //   ]
  // Overall evaluation: ((T1 AND T2 OR T3)) OR (T4)
  const thresholdConfigs: any[][] = [];

  if (selectedBrands && selectedBrands.length > 0) {
    selectedBrands.forEach(brand => {
      if (brand.thresholds && brand.thresholds.length > 0) {
        // Group thresholds based on logical operators
        // Each group is an inner array where conditions are connected via connect_to_next
        // Groups are OR'd together (outer array)
        let currentGroup: any[] = [];

        brand.thresholds.forEach((threshold: any, index: number) => {
          // Check if this is a pacing threshold (has dimension and metric), EMQ threshold (has pixel and event), or taxonomy threshold (has taxonomyType)
          const isPacingThreshold = threshold.dimension && threshold.metric;
          const isEMQThreshold = threshold.pixelId && threshold.eventName;
          const isTaxonomyThreshold = threshold.taxonomyType && threshold.taxonomyValueType;

          if (isPacingThreshold) {
            // Handle pacing threshold with new format (arrays for metric and dimension)
            const thresholdValue =
              typeof threshold.threshold === 'number'
                ? threshold.threshold
                : Number(threshold.threshold) || 0;

            // Convert dates from YYYY-MM-DD to DD/MM/YYYY format
            const formatDate = (dateStr: string | null | undefined) => {
              if (!dateStr) return null;
              const date = new Date(dateStr);
              // Check if date is valid
              if (isNaN(date.getTime())) return null;
              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = date.getFullYear();
              // Double check for NaN values
              if (isNaN(Number(day)) || isNaN(Number(month)) || isNaN(Number(year))) {
                return null;
              }
              return `${day}/${month}/${year}`;
            };

            // Build metric array (new format)
            const metricArray = threshold.metric
              ? [
                  {
                    name: threshold.metric,
                    metrics_id: threshold.metricsId || null,
                    type: threshold.metricsCategory || 'standard',
                  },
                ]
              : [];

            // Build dimension array (new format)
            // Each subcategory becomes a separate dimension object
            const normalizeDimensionKind = (kind?: string) => {
              if (!kind) return 'standard';
              const lowered = kind.toLowerCase();
              return lowered === 'custom' || lowered === 'grouping' ? lowered : 'standard';
            };

            // Check if dimensionValue is already in the final payload format
            // This happens when editing an existing alert that was saved with sheet config dimensions
            let directDimensionArray: any[] | null = null;
            if (threshold.dimensionValue) {
              try {
                const parsed =
                  typeof threshold.dimensionValue === 'string'
                    ? JSON.parse(threshold.dimensionValue)
                    : threshold.dimensionValue;

                // Check if it's an array of objects with the required fields (name, type, category)
                if (
                  Array.isArray(parsed) &&
                  parsed.length > 0 &&
                  parsed.every(
                    (item: any) =>
                      item &&
                      typeof item === 'object' &&
                      typeof item.name === 'string' &&
                      typeof item.type === 'string' &&
                      typeof item.category === 'string'
                  )
                ) {
                  // Already in correct format; normalize All Dimension to use type ALL_DIMENSION for API
                  directDimensionArray = parsed.map((item: any) =>
                    item.name === 'All' && (item.type === 'All' || item.type === 'ALL_DIMENSION')
                      ? { ...item, type: 'ALL_DIMENSION' }
                      : item
                  );
                }
              } catch {
                // Not valid JSON, continue with normal processing
              }
            }

            // Track parent-child pairs with metadata
            interface DimensionEntry {
              parent: string;
              child: string;
              category: string;
              type: string;
            }

            const dimensionEntries: DimensionEntry[] = [];

            // Preferred source: comma-separated dimension string like "Platform > Impact, Type > Video"
            // Special case: "All Dimension" should be handled separately
            if (threshold.dimension) {
              const category = normalizeDimensionKind(
                threshold.dimensionCategory || threshold.dimensionType
              );

              // Check if "All Dimension" is selected
              const dimensionStr = threshold.dimension.trim();
              if (dimensionStr === 'All Dimension' || dimensionStr === 'All') {
                // Special case: "All Dimension" selected
                dimensionEntries.push({
                  parent: 'All',
                  child: 'All',
                  category: 'standard',
                  type: 'All',
                });
              } else {
                threshold.dimension
                  .split(',')
                  .map((d: string) => d.trim())
                  .filter(Boolean)
                  .forEach((entry: string) => {
                    if (entry.includes('>')) {
                      const [parentRaw, childRaw] = entry.split('>').map((p: string) => p.trim());
                      if (parentRaw && childRaw) {
                        dimensionEntries.push({
                          parent: parentRaw,
                          child: childRaw,
                          category: category,
                          type: parentRaw, // Use parent name as type
                        });
                      }
                    }
                    // Skip entries without '>' as they're incomplete
                  });
              }
            }

            // Fallback: dimensionValue may contain JSON (array of strings or objects)
            if (dimensionEntries.length === 0 && threshold.dimensionValue) {
              const rawDimensionValue =
                typeof threshold.dimensionValue === 'string'
                  ? (() => {
                      try {
                        return JSON.parse(threshold.dimensionValue);
                      } catch {
                        return threshold.dimensionValue;
                      }
                    })()
                  : threshold.dimensionValue;

              const category = normalizeDimensionKind(
                threshold.dimensionCategory || threshold.dimensionType
              );
              const parentKey =
                threshold.dimension && threshold.dimension.includes('>')
                  ? threshold.dimension.split('>')[0].trim()
                  : threshold.dimension || 'dimension';

              // Check if "All Dimension" is in the dimensionValue
              if (
                threshold.dimension === 'All Dimension' ||
                threshold.dimension === 'All' ||
                (Array.isArray(rawDimensionValue) &&
                  rawDimensionValue.some(
                    (val: any) =>
                      val === 'All Dimension' ||
                      val === 'All' ||
                      (typeof val === 'object' &&
                        (val.value === 'All Dimension' ||
                          val.value === 'All' ||
                          val.name === 'All Dimension' ||
                          val.name === 'All'))
                  ))
              ) {
                dimensionEntries.push({
                  parent: 'All',
                  child: 'All',
                  category: 'standard',
                  type: 'All',
                });
              } else if (Array.isArray(rawDimensionValue)) {
                rawDimensionValue.forEach((val: any) => {
                  if (typeof val === 'string') {
                    dimensionEntries.push({
                      parent: parentKey,
                      child: val,
                      category: category,
                      type: parentKey,
                    });
                  } else if (val && typeof val === 'object') {
                    // Handle new payload format: { name, type, value, category, dimension_id }
                    // Also handle old format: { child, parent, value }
                    const child =
                      val.name || val.child || (Array.isArray(val.value) ? null : val.value) || val;
                    const parent = val.type || val.parent || parentKey;
                    const valCategory = val.category || category;

                    // Skip if child is still an array or invalid
                    if (child && typeof child === 'string') {
                      dimensionEntries.push({
                        parent: parent,
                        child: child,
                        category: valCategory,
                        type: parent,
                      });
                    }
                  }
                });
              }
            }

            // Create one dimension object per child
            // For custom dimensions, value array should contain the values
            // For standard dimensions, value array should be empty
            // Special case: "All Dimension" should have name: "All", type: "All", value: [], category: "standard"
            // If directDimensionArray is available (already in correct format), use it directly
            const dimensionArray = directDimensionArray
              ? directDimensionArray
              : dimensionEntries.map(entry => {
                  const isCustom = entry.category === 'custom';
                  // Check if this is "All Dimension" - backend expects type "ALL_DIMENSION"
                  const isAllDimension = entry.child === 'All' && entry.type === 'All';

                  return {
                    name: entry.child, // Child value becomes the name
                    type: isAllDimension ? 'ALL_DIMENSION' : entry.type, // Backend expects ALL_DIMENSION for all dimensions
                    category: entry.category,
                    value: isAllDimension ? [] : isCustom ? [entry.child] : [], // All Dimension and standard dimensions have empty array, custom have values
                    dimension_id: isAllDimension ? null : threshold.dimensionId || null, // All Dimension has null dimension_id
                  };
                });

            // Check if this is a relative calendar type
            const isRelativeCalendar = threshold.calendarType === 'relative';

            // Extract dates from endDate (format: "startISO|endISO")
            // This format is used for both calendar and fiscal modes
            const hasDates =
              !isRelativeCalendar && threshold.endDate && threshold.endDate.includes('|');
            let startDateFormatted: string | null = null;
            let endDateFormatted: string | null = null;

            if (hasDates) {
              const [startISO, endISO] = threshold.endDate.split('|');
              startDateFormatted = formatDate(startISO);
              endDateFormatted = formatDate(endISO);
            }

            const thresholdConfig: any = {
              brand_id: brand.brandId,
              operator: threshold.operator ?? '+',
              value: thresholdValue,
              metric: metricArray,
              dimension: dimensionArray.length > 0 ? dimensionArray : undefined,
              // For relative calendar type: send comparision_date and null dates
              ...(isRelativeCalendar
                ? {
                    comparision_date: threshold.comparisonDate || threshold.startDate || null,
                    start_date: null,
                    end_date: null,
                    calendar_type: 'relative',
                  }
                : {
                    // Always send start_date and end_date (actual dates, not preset identifier)
                    ...(hasDates && startDateFormatted && endDateFormatted
                      ? {
                          start_date: startDateFormatted,
                          end_date: endDateFormatted,
                        }
                      : {}),
                    // Add calendar_type from threshold's calendarType field (only for pacing alerts with date ranges)
                    // Default to 'calendar' if not set
                    ...(threshold.startDate || threshold.endDate
                      ? {
                          calendar_type: threshold.calendarType || 'calendar',
                        }
                      : {}),
                  }),
            };

            // Add pacing_value_type: backend expects "percentage" | "count" (default "percentage")
            // Map UI "number" -> API "count"; UI "percentage" (or missing) -> "percentage"
            const pacingValueTypeApi =
              threshold.pacingValueType === 'number'
                ? 'count'
                : threshold.pacingValueType || 'percentage';
            thresholdConfig.pacing_value_type = pacingValueTypeApi;

            // Add connect_to_next field if this threshold has a logical operator
            const isLastThreshold = index === brand.thresholds.length - 1;
            const logicalOp = threshold.logicalOperator;

            if (!isLastThreshold && logicalOp && logicalOp !== 'NONE') {
              thresholdConfig.connect_to_next = logicalOp;
            } else {
              thresholdConfig.connect_to_next = null;
            }

            currentGroup.push(thresholdConfig);

            if (isLastThreshold || logicalOp === 'NONE' || !logicalOp) {
              if (currentGroup.length > 0) {
                thresholdConfigs.push(currentGroup);
                currentGroup = [];
              }
            }
          } else if (isTaxonomyThreshold) {
            // Handle taxonomy threshold
            // For "updated_at" type, keep value as string (ISO date)
            // For "percentage" and "count", convert to number
            const thresholdValue =
              threshold.taxonomyValueType === 'updated_at'
                ? threshold.threshold // Keep as string for date/time
                : typeof threshold.threshold === 'number'
                  ? threshold.threshold
                  : Number(threshold.threshold) || 0;

            const thresholdConfig: any = {
              brand_id: brand.brandId,
              metric: 'taxonomy_score',
              operator: threshold.operator ?? '<',
              value: thresholdValue,
              taxonomy_type: threshold.taxonomyType,
              taxonomy_value_type: threshold.taxonomyValueType,
              connect_to_next: null,
            };

            const isLastThreshold = index === brand.thresholds.length - 1;
            const logicalOp = threshold.logicalOperator;

            if (!isLastThreshold && logicalOp && logicalOp !== 'NONE') {
              thresholdConfig.connect_to_next = logicalOp;
            }

            currentGroup.push(thresholdConfig);

            if (isLastThreshold || logicalOp === 'NONE' || !logicalOp) {
              if (currentGroup.length > 0) {
                thresholdConfigs.push(currentGroup);
                currentGroup = [];
              }
            }
          } else if (isEMQThreshold) {
            // Handle EMQ threshold
            const metric = formData.alertMetric
              ? formData.alertMetric
              : formData.toolName
                ? formData.toolName.toLowerCase().replace(/\s+/g, '_')
                : 'avg_composite_score';

            const thresholdValue =
              typeof threshold.threshold === 'number'
                ? threshold.threshold
                : Number(threshold.threshold) || 0;

            const thresholdConfig: any = {
              brand_id: brand.brandId,
              metric: metric,
              operator: threshold.operator ?? '<',
              value: thresholdValue,
              comparison_period: 'day',
              pixel_id: threshold.pixelId,
              pixel_name: threshold.pixelName || '',
              event_name: threshold.eventName,
              event_id: threshold.eventId,
            };

            const isLastThreshold = index === brand.thresholds.length - 1;
            const logicalOp = threshold.logicalOperator;

            if (!isLastThreshold && logicalOp && logicalOp !== 'NONE') {
              thresholdConfig.connect_to_next = logicalOp;
            }

            currentGroup.push(thresholdConfig);

            if (isLastThreshold || logicalOp === 'NONE' || !logicalOp) {
              if (currentGroup.length > 0) {
                thresholdConfigs.push(currentGroup);
                currentGroup = [];
              }
            }
          } else {
            // If threshold is incomplete, close current group and start fresh
            if (currentGroup.length > 0) {
              thresholdConfigs.push(currentGroup);
              currentGroup = [];
            }
          }
        });

        // Add any remaining group
        if (currentGroup.length > 0) {
          thresholdConfigs.push(currentGroup);
        }
      }
    });
  }

  // Warn if no valid thresholds were configured
  if (thresholdConfigs.length === 0 || thresholdConfigs.every(group => group.length === 0)) {
    console.warn('No valid thresholds configured, using default threshold');
    const metric = formData.alertMetric
      ? formData.alertMetric
      : formData.toolName
        ? formData.toolName.toLowerCase().replace(/\s+/g, '_')
        : 'avg_composite_score';

    thresholdConfigs.push([
      {
        brand_id: resolvedBrandIds[0] ?? 1,
        metric: metric,
        operator: '<',
        value: 8,
        comparison_period: 'day',
        pixel_id: '',
        pixel_name: '',
        event_name: '',
        event_id: undefined,
      },
    ]);
  }

  return thresholdConfigs;
};

export const mapAlertFormDataToApi = (
  formData: any,
  userId: number,
  selectedBrands?: any[],
  alertTypeId?: number,
  userEmail?: string,
  originalAlert?: {
    recipients?: {
      users?: Array<{ name: string; email: string; user_id: number; subscriber: boolean }>;
    };
  },
  teamUsers?: Array<{ id: number; email: string }>
): any => {
  // Get brand IDs from selected brands
  const brandIds = selectedBrands?.map(brand => brand.brandId) ?? [];

  if (brandIds.length === 0) {
    console.warn('No brands selected, using default brand ID');
  }

  const thresholdConfigs = buildThresholdConfigs(formData, selectedBrands, brandIds);

  // Parse Slack recipients: prefer JSON payload with { type, id, name }
  const slackRecipients = (formData.slackRecipients ?? [])
    .map((recipient: string) => {
      try {
        const parsed = JSON.parse(recipient);
        if (parsed && parsed.id && parsed.type) {
          if (parsed.type === 'user') {
            const slackId = String(parsed.id);
            let mappedUserId = parsed.userId ?? parsed.user_id ?? null;
            // If userId is still null but email exists, try to look it up from teamUsers
            if (!mappedUserId && parsed.email && teamUsers && teamUsers.length > 0) {
              const teamUser = teamUsers.find(
                tu => tu.email?.toLowerCase() === parsed.email?.toLowerCase()
              );
              mappedUserId = teamUser?.id ?? null;
            }
            return {
              user_id: mappedUserId,
              recipient_id: slackId,
              recipient_type: 'slack_user',
              recipient_value: parsed.name || slackId, // send name
            };
          }
          if (parsed.type === 'channel') {
            const recipientId = String(parsed.id);
            return {
              user_id: null,
              recipient_id: recipientId,
              recipient_type: 'slack_channel',
              recipient_value: parsed.name || String(parsed.id), // send name like #channel
            };
          }
        }
      } catch {
        // Non-JSON value; fall back to legacy parsing below
      }
      // Backward-compat for old values like user_U090... / channel_C09...
      if (recipient.startsWith('user_')) {
        const slackId = recipient.replace('user_', '');
        return {
          user_id: null,
          recipient_id: slackId,
          recipient_type: 'slack_user',
          recipient_value: slackId, // no name available
        };
      } else if (recipient.startsWith('channel_')) {
        const channelId = recipient.replace('channel_', '');
        return {
          user_id: null,
          recipient_id: channelId,
          recipient_type: 'slack_channel',
          recipient_value: channelId, // no name available
        };
      }
      return null;
    })
    .filter(Boolean);

  // Check communication methods first (need for both web recipients and enable flag)
  const slackMethod = formData.communicationMethods?.find(
    (m: any) => m.id === '1' || m.type === 'Slack'
  );
  const emailMethod = formData.communicationMethods?.find(
    (m: any) => m.id === '2' || m.type === 'Email'
  );
  const liftNotificationWebMethod = formData.communicationMethods?.find(
    (m: any) => m.id === '3' || m.type === 'LIFT notifications for a Brand'
  );
  const liftNotificationUserMethod = formData.communicationMethods?.find(
    (m: any) => m.id === '4' || m.type === 'LIFT notifications for a user'
  );

  // Create recipients structure
  // web_brand recipients (from selected brands) - only if LIFT notifications for a Brand is selected
  const webBrandRecipients =
    liftNotificationWebMethod?.selected && !liftNotificationUserMethod?.selected
      ? (selectedBrands?.map(brand => ({
          user_id: userId,
          recipient_id: brand.brandId,
          brand_id: brand.brandId,
          brand_name: brand.brandName ?? 'Default Brand',
          recipient_type: 'web_brand',
          recipient_value: { brand_id: brand.brandId },
        })) ?? [])
      : [];

  // web_user recipients - only if LIFT Notification User is selected
  const webUserRecipients =
    liftNotificationUserMethod?.selected && !liftNotificationWebMethod?.selected
      ? (formData.liftNotificationUserRecipients || [])
          .map((value: string) => {
            try {
              const parsed = JSON.parse(value);
              const recipientId = parsed?.id ?? parsed?.user_id ?? null;

              if (recipientId) {
                return {
                  user_id: recipientId,
                  recipient_id: recipientId,
                  recipient_type: 'web_user',
                  recipient_value: {
                    name: parsed?.name || 'User',
                    user_id: recipientId,
                    email: parsed?.email,
                  },
                };
              }
            } catch {
              // Not JSON, skip
            }
            return null;
          })
          .filter(Boolean)
      : [];

  // Combine web recipients (either brand OR user, never both)
  const webRecipients = [...webBrandRecipients, ...webUserRecipients];

  if (
    webRecipients.length === 0 &&
    (liftNotificationWebMethod?.selected || liftNotificationUserMethod?.selected)
  ) {
    console.warn('No web recipients selected, using default brand');
    webRecipients.push({
      user_id: userId,
      recipient_id: 1,
      brand_id: 1,
      brand_name: 'Default Brand',
      recipient_type: 'web_brand',
      recipient_value: { brand_id: 1 },
    });
  }

  const parsedEmailRecipients = (formData.emailRecipients ?? []).map((value: string) => {
    try {
      const parsed = JSON.parse(value);
      if (parsed && parsed.email) {
        const recipientId = parsed?.id ?? parsed?.user_id ?? null;
        return {
          user_id: recipientId,
          recipient_id: recipientId,
          recipient_type: 'email_user',
          recipient_value: {
            name: parsed.name ?? 'User',
            email: parsed.email,
          },
        };
      }
    } catch {
      // Not JSON, treat as raw email string
    }
    return {
      user_id: null,
      recipient_id: null,
      recipient_type: 'email_user',
      recipient_value: {
        name: 'User',
        email: value,
      },
    };
  });

  const emailRecipients = parsedEmailRecipients;

  // Parse CC email recipients (same format as To: email, but recipient_type: email_cc_user)
  const parsedEmailCcRecipients = (formData.emailCcRecipients ?? []).map((value: string) => {
    try {
      const parsed = JSON.parse(value);
      if (parsed && parsed.email) {
        const recipientId = parsed?.id ?? parsed?.user_id ?? null;
        return {
          user_id: recipientId,
          recipient_id: recipientId,
          recipient_type: 'email_cc_user',
          recipient_value: {
            name: parsed.name ?? 'User',
            email: parsed.email,
          },
        };
      }
    } catch {
      // Not JSON, treat as raw email string
    }
    return {
      user_id: null,
      recipient_id: null,
      recipient_type: 'email_cc_user',
      recipient_value: {
        name: 'User',
        email: value,
      },
    };
  });

  const emailCcRecipients = parsedEmailCcRecipients;

  // Users array: preserve original recipients.users if editing, otherwise use current user
  const userRecipients: Array<{
    name: string;
    email: string;
    user_id: number;
    subscriber: boolean;
  }> =
    originalAlert?.recipients?.users && originalAlert.recipients.users.length > 0
      ? originalAlert.recipients.users
      : userEmail && userId
        ? [{ name: 'User', email: userEmail, user_id: userId, subscriber: true }]
        : [];

  // Build recipients object - always include slack, email, email_cc, and web arrays
  const recipients: {
    web: typeof webRecipients;
    email: typeof emailRecipients;
    email_cc: typeof emailCcRecipients;
    slack: typeof slackRecipients;
    users?: typeof userRecipients;
  } = {
    // Always include web recipients (empty array if not selected)
    web:
      liftNotificationWebMethod?.selected || liftNotificationUserMethod?.selected
        ? webRecipients
        : [],
    // Always include email recipients (empty array if not selected)
    email: emailMethod?.selected ? emailRecipients : [],
    // CC recipients only when email is selected
    email_cc: emailMethod?.selected ? emailCcRecipients : [],
    // Always include slack recipients (empty array if not selected)
    slack: slackMethod?.selected ? slackRecipients : [],
  };

  // Always include users array with subscribe: true by default (for subscribers)
  if (userRecipients.length > 0) {
    recipients.users = userRecipients;
  }

  // Generate cron expression based on cadence and schedule_config
  const generateCronExpression = (
    cadence: string,
    scheduleConfig?: {
      time?: string;
      days?: number[];
      day?: number | number[];
    }
  ): string => {
    // Parse time to get hour and minute (format: "HH:MM")
    const time = scheduleConfig?.time ?? '09:00';
    const [hour = '9', minute = '0'] = time.split(':');

    switch (cadence) {
      case 'realtime': {
        // For realtime, check at specified time on specified days
        // If days are specified, use them; otherwise check every day
        const realtimeDays = scheduleConfig?.days;
        if (realtimeDays && realtimeDays.length > 0 && realtimeDays.length < 7) {
          // Specific days: use day-of-week format (1=Monday, 7=Sunday)
          const cronDays = realtimeDays.join(',');
          return `${minute} ${hour} * * ${cronDays}`;
        }
        // Every day at specified time
        return `${minute} ${hour} * * *`;
      }
      case 'hourly':
        return `${minute} * * * *`; // Every hour at specified minute
      case '12_hourly':
        return `${minute} */12 * * *`; // Every 12 hours at specified minute
      case 'daily': {
        // For daily cadence, always use all days (*) regardless of days array
        // The days array is only used for weekly cadence
        return `${minute} ${hour} * * *`; // Every day at specified time
      }
      case 'weekly': {
        const days = scheduleConfig?.days;
        if (days && days.length > 0) {
          // Convert days array to cron format
          const cronDays = days.length === 1 ? days[0].toString() : days.join(',');
          return `${minute} ${hour} * * ${cronDays}`;
        }
        return `${minute} ${hour} * * 1`; // Default to Monday
      }
      case 'monthly': {
        const dayOfMonth = scheduleConfig?.day;
        if (Array.isArray(dayOfMonth) && dayOfMonth.length > 0) {
          // Multiple days of month
          const cronDays = dayOfMonth.join(',');
          return `${minute} ${hour} ${cronDays} * *`;
        }
        // Single day or default
        const singleDay = Array.isArray(dayOfMonth) ? dayOfMonth[0] : (dayOfMonth ?? 1);
        return `${minute} ${hour} ${singleDay} * *`;
      }
      default:
        return `${minute} ${hour} * * 1-5`; // Default to weekdays at specified time
    }
  };

  // Enable lift notification if either web or user method is selected
  const liftMethod =
    liftNotificationWebMethod?.selected || liftNotificationUserMethod?.selected
      ? { selected: true }
      : undefined;

  // Build message_template object - send empty string for non-selected channels
  // Convert HTML from Quill editor to appropriate format for each channel
  const message_template: {
    slack: string;
    email: string;
    web: string;
    msg: string;
  } = {
    // Slack: Convert to Slack markdown format (preserves bold, italic, strikethrough, newlines)
    slack: slackMethod?.selected
      ? convertHtmlToSlackMarkdown(formData.messageTemplates?.slack ?? formData.alertMessage ?? '')
      : '',
    // Email: Keep as HTML (backend expects html_content for SendGrid)
    email: emailMethod?.selected
      ? convertHtmlToEmailHtml(formData.messageTemplates?.email ?? formData.alertMessage ?? '')
      : '',
    // Web: Keep as HTML to preserve editor spacing/line breaks
    web: liftMethod?.selected
      ? convertHtmlToEmailHtml(formData.messageTemplates?.web ?? formData.alertMessage ?? '')
      : '',
    // Msg: Plain text message template (required field)
    // Always use the Alert Prompt Message value, not the channel-specific templates
    msg: convertHtmlToPlainText(formData.alertMessage ?? ''),
  };

  // Determine alert type ID
  const finalAlertTypeId = alertTypeId ?? formData.alertTypeId ?? 1;
  if (!alertTypeId && !formData.alertTypeId) {
    console.warn('No alert type ID provided, using default value 1');
  }

  // Always send realtime as cadence
  const finalCadence = 'realtime';

  // Ensure timezone is in IANA format when sending to backend
  const timezone = normalizeTimezone(formData.scheduleConfig?.timezone ?? 'America/New_York');

  // Build schedule_config object
  // Use formData.cadence for schedule config defaults (even though we always send 'realtime' as cadence)
  const formDataCadence = formData.cadence ?? 'realtime';
  const schedule_config = {
    time: formData.scheduleConfig?.time ?? '09:00',
    timezone: timezone,
    days:
      formData.scheduleConfig?.days ??
      (formDataCadence === 'weekly'
        ? [1, 2, 3, 4, 5]
        : formDataCadence === 'daily'
          ? [1, 2, 3, 4, 5, 6, 7]
          : undefined),
    // Always send day: 1 for monthly (actual schedule is in cron_info)
    day: formDataCadence === 'monthly' ? 1 : undefined,
  };

  // Final API payload structure
  // Example payload with nested threshold_configs:
  // {
  //   "user_id": 1,
  //   "name": "Multi-Brand Alert",
  //   "brand_ids": [10, 20],
  //   "threshold_configs": [
  //     [  // Group 1 - conditions connected with AND/OR
  //       {
  //         "brand_id": 10,
  //         "metric": "composite_score",
  //         "operator": "<",
  //         "value": 0.75,
  //         "pixel_id": "1234567890",
  //         "event_name": "Purchase",
  //         "connect_to_next": "AND"  // Connects to next condition in same group
  //       },
  //       {
  //         "brand_id": 10,
  //         "metric": "composite_score",
  //         "operator": ">",
  //         "value": 0.50,
  //         "pixel_id": "1234567890",
  //         "event_name": "Purchase"
  //         // No connect_to_next - last in group
  //       }
  //     ],
  //     [  // Group 2 - separate condition (OR'd with Group 1)
  //       {
  //         "brand_id": 20,
  //         "metric": "composite_score",
  //         "operator": "<",
  //         "value": 0.80,
  //         "pixel_id": "9876543210",
  //         "event_name": "Purchase"
  //       }
  //     ]
  //   ],
  //   // ... rest of payload
  // }
  // Evaluation: (Group 1) OR (Group 2) = ((T1 AND T2)) OR (T3)

  return {
    user_id: userId,
    name: formData.alertName,
    description: formData.description,
    brand_ids: brandIds.length > 0 ? brandIds : [1],
    alert_type_id: finalAlertTypeId,
    threshold_configs: thresholdConfigs,
    cadence: finalCadence,
    schedule_config,
    enable_slack: slackMethod?.selected ?? false,
    enable_email: emailMethod?.selected ?? false,
    enable_lift_notification: liftMethod?.selected ?? false,
    message_template,
    recipients: recipients,
    scheduler_job_data: {
      time_zone: timezone,
    },
    // Use formData.scheduleConfig for cron generation (has user's actual day selection)
    // Don't use schedule_config which has day hardcoded to 1
    cron_info: generateCronExpression(formDataCadence, {
      time: formData.scheduleConfig?.time,
      days: formData.scheduleConfig?.days,
      day: formData.scheduleConfig?.day,
    }),
    schedule_type: 'threshold',
  };
};
